import { useEffect, useMemo, useRef } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { models } from 'powerbi-client'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  PowerBiReportEmbed,
  PowerBiSavedFiltersConfig,
  PowerBiSlicerConfig,
} from '@/components/common/PowerBiReportEmbed'
import { usePowerBiDashboard } from '@/hooks/usePowerBiDashboard'
import { PlanDetailFilterTarget } from '@/config/powerbiReports'
import { toast } from '@/stores/useToastStore'

const REPORT_TYPE = 'CAT' as const

function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
  }
  if (error instanceof Error) return error.message
  return 'Failed to load report'
}

interface PlanDetailReportTabProps {
  reportId: string
  pageId: string
  label: string
  filterTarget: PlanDetailFilterTarget
  filterValue: string
  versionFilterTarget?: PlanDetailFilterTarget
  versionFilterValue?: string
  slicerTitle?: string
  showPageNavigation?: boolean
  savedFilters?: PowerBiSavedFiltersConfig
}

export function PlanDetailReportTab({
  reportId,
  pageId,
  label,
  filterTarget,
  filterValue,
  versionFilterTarget,
  versionFilterValue,
  slicerTitle,
  showPageNavigation = false,
  savedFilters,
}: PlanDetailReportTabProps) {
  const lastToastMessageRef = useRef<string | null>(null)

  const filters = useMemo<models.IBasicFilter[] | undefined>(() => {
    const result: models.IBasicFilter[] = []

    if (!slicerTitle) {
      result.push({
        $schema: 'http://powerbi.com/product/schema#basic',
        target: filterTarget,
        operator: 'In',
        values: [filterValue],
        filterType: models.FilterType.Basic,
      })
    }

    if (versionFilterTarget && versionFilterValue) {
      result.push({
        $schema: 'http://powerbi.com/product/schema#basic',
        target: versionFilterTarget,
        operator: 'In',
        values: [versionFilterValue],
        filterType: models.FilterType.Basic,
      })
    }

    return result.length > 0 ? result : undefined
  }, [
    filterValue,
    filterTarget,
    versionFilterTarget,
    versionFilterValue,
    slicerTitle,
  ])

  const slicer = useMemo<PowerBiSlicerConfig | undefined>(
    () =>
      slicerTitle
        ? {
            title: slicerTitle,
            target: filterTarget,
            values: [filterValue],
          }
        : undefined,
    [slicerTitle, filterTarget, filterValue]
  )

  const {
    data: embedConfig,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = usePowerBiDashboard(reportId, REPORT_TYPE)

  useEffect(() => {
    if (!isError) return

    const message = getApiErrorMessage(error)

    if (lastToastMessageRef.current !== message) {
      lastToastMessageRef.current = message
      toast.error(message)
    }
  }, [error, isError])

  const handleRefresh = () => {
    lastToastMessageRef.current = null
    void refetch()
  }

  if (isLoading || (!isError && versionFilterTarget && !versionFilterValue)) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardContent className="flex min-h-0 flex-1 items-center justify-center p-0">
          <div className="flex flex-col items-center gap-3 text-center">
            <RefreshCw className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm font-medium text-foreground">
              Loading {label} report
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !embedConfig) {
    return (
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CardContent className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-6 w-6" />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-foreground">
              Unable to load report
            </h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              {getApiErrorMessage(error)}
            </p>
          </div>
          <Button type="button" onClick={handleRefresh} disabled={isFetching}>
            Try again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <CardContent className="flex min-h-0 flex-1 flex-col p-0">
        <PowerBiReportEmbed
          // Reset readiness, refs and the iframe together when the scope changes.
          key={JSON.stringify([reportId, pageId, filters, slicer])}
          embedConfig={embedConfig}
          reportType={REPORT_TYPE}
          pageName={pageId}
          filters={filters}
          slicer={slicer}
          showPageNavigation={showPageNavigation}
          savedFilters={savedFilters}
          loadingLabel={`${label} report`}
          reportLabel={label}
          onRefresh={handleRefresh}
          isFetching={isFetching}
        />
      </CardContent>
    </Card>
  )
}
